<template>
  <div class="bg-gray-50 p-4">
    <div class="bg-white rounded-lg border border-gray-200">
      <!-- Header -->
      <div class="flex items-center gap-3 px-3.5 py-2.5 border-b border-gray-100">
        <span style="font-size: 13px;" class="font-semibold text-gray-900">Work types</span>
        <span style="font-size: 11px;" class="text-gray-500">{{ workTypes.length }} configured</span>
        <span class="flex-1" />
        <div class="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded" style="font-size: 12px;">
          <font-awesome-icon :icon="['fas', 'search']" class="text-gray-400" style="font-size: 10px;" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search…"
            class="bg-transparent border-none outline-none text-gray-700 placeholder-gray-400 w-40"
            style="font-size: 12px;"
          />
        </div>
        <button @click="showAIGeneratorModal = true" style="font-size: 12px;" class="text-gray-500 hover:text-gray-700 font-medium flex items-center gap-1">
          <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" style="font-size: 10px;" /> AI Generate
        </button>
        <button @click="showWorkTypeModal = true" style="font-size: 12px;" class="text-gray-700 hover:text-gray-900 font-medium flex items-center gap-1">
          <font-awesome-icon :icon="['fas', 'plus']" style="font-size: 10px;" /> Add
        </button>
      </div>

      <!-- Table -->
      <div v-if="loading" class="py-8 text-center text-gray-400" style="font-size: 12px;">Loading…</div>
      <div v-else-if="filteredWorkTypes.length === 0" class="py-8 text-center text-gray-400" style="font-size: 12px;">
        {{ searchQuery ? 'No work types match your search' : 'No work types configured' }}
      </div>
      <table v-else class="w-full" style="font-size: 13px;">
        <thead>
          <tr class="border-b border-gray-200 bg-gray-50">
            <th
              v-for="column in sortableColumns"
              :key="column.key"
              :aria-sort="sortAria(column.key)"
              class="text-left px-3.5 py-2 font-medium text-gray-500 uppercase tracking-wide"
              style="font-size: 11px;"
            >
              <button
                type="button"
                :data-test="`sort-${column.key}`"
                class="inline-flex items-center gap-1 uppercase tracking-wide hover:text-gray-900"
                :class="sortKey === column.key ? 'text-gray-900' : ''"
                @click="toggleSort(column.key)"
              >
                {{ column.label }}
                <font-awesome-icon
                  :icon="sortIcon(column.key)"
                  :class="sortKey === column.key ? 'text-gray-700' : 'text-gray-300'"
                  style="font-size: 9px;"
                />
              </button>
            </th>
            <th class="text-left px-3.5 py-2 font-medium text-gray-500 uppercase tracking-wide" style="font-size: 11px;">Config</th>
            <th class="text-right px-3.5 py-2 font-medium text-gray-500 uppercase tracking-wide w-20" style="font-size: 11px;"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="wt in sortedWorkTypes"
            :key="wt.id"
            class="group border-b border-gray-100 hover:bg-gray-50"
          >
            <td class="px-3.5 py-2.5">
              <span class="font-medium text-gray-900">{{ wt.display_name || wt.name }}</span>
              <span
                v-if="wt.is_system"
                v-tooltip="systemTooltip"
                data-test="system-badge"
                tabindex="0"
                style="font-size: 10px;"
                class="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium align-middle cursor-default"
              >
                <font-awesome-icon :icon="['fas', 'lock']" style="font-size: 8px;" /> System
              </span>
            </td>
            <td class="px-3.5 py-2.5 text-gray-500 font-mono" style="font-size: 12px;">{{ wt.name }}</td>
            <td class="px-3.5 py-2.5">
              <span v-if="wt.abbreviated_name" class="font-mono px-1.5 py-0.5 bg-gray-100 rounded text-gray-700" style="font-size: 11px;">{{ wt.abbreviated_name }}</span>
              <span v-else class="text-gray-300">—</span>
            </td>
            <td class="px-3.5 py-2.5">
              <button
                v-if="wt.configuration && Object.keys(wt.configuration).length"
                @click="viewConfiguration(wt)"
                style="font-size: 11px;"
                class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 hover:text-gray-900 hover:bg-gray-200"
              >
                <font-awesome-icon :icon="['fas', 'code']" style="font-size: 9px;" />
                {{ Object.keys(wt.configuration).length }} keys
              </button>
              <span v-else class="text-gray-300" style="font-size: 11px;">empty</span>
            </td>
            <td class="px-3.5 py-2.5 text-right">
              <div v-if="!wt.is_system" data-test="row-actions" class="flex items-center justify-end gap-1">
                <button @click="aiEditWorkType(wt)" style="font-size: 11px;" class="px-2 py-0.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded font-medium flex items-center gap-1 whitespace-nowrap">
                  <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" style="font-size: 9px;" /> AI Edit
                </button>
                <button @click="editWorkType(wt)" style="font-size: 11px;" class="px-2 py-0.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded font-medium">Edit</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- AI Generator Modal -->
    <transition name="modal">
      <div v-if="showAIGeneratorModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">{{ aiEditingId ? 'Edit Work Type Configuration with AI' : 'Generate Work Type Configuration with AI' }}</h3>
          </div>

          <!-- Generation Step -->
          <div v-if="!aiGenerated" class="p-4 space-y-3">
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Describe what you want to collect</label>
              <textarea v-model="aiDescription" rows="4" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500" placeholder="Example: Create a form to collect patient medication adherence information…"></textarea>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">Work Type Name *</label>
                <input v-model="aiWorkTypeName" type="text" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500" placeholder="e.g., Medication Adherence Check">
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">Abbreviation</label>
                <input v-model="aiAbbreviation" type="text" maxlength="10" class="w-32 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 font-mono" placeholder="e.g., MAC" @input="aiAbbreviation = aiAbbreviation.toUpperCase()">
              </div>
            </div>
            <div v-if="aiError" class="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{{ aiError }}</div>
            <div class="flex justify-end gap-2">
              <button @click="closeAIGenerator" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
              <button @click="generateWithAI" :disabled="!aiDescription || !aiWorkTypeName || aiGenerating" class="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">
                <font-awesome-icon v-if="aiGenerating" :icon="['fas', 'spinner']" class="animate-spin mr-1" />
                {{ aiGenerating ? 'Generating…' : 'Generate' }}
              </button>
            </div>
          </div>

          <!-- Review Step -->
          <div v-else class="flex flex-col flex-1 overflow-hidden">
            <div class="p-4 space-y-3 overflow-y-auto">
              <!-- Editing hint (edit mode, before any AI regeneration) -->
              <div v-if="aiEditingId" class="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-800">
                Editing <span class="font-semibold">{{ aiWorkTypeName }}</span>. Describe one change below and hit Apply change — existing fields are left alone. Or edit the JSON directly. Save changes when done.
              </div>

              <!-- What changed (diff) — surfaces unrequested fields immediately -->
              <div v-if="aiDiff" class="rounded border p-3" :class="diffLooksSuspicious ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-200'">
                <div class="flex items-start gap-2">
                  <font-awesome-icon
                    :icon="['fas', diffLooksSuspicious ? 'exclamation-triangle' : 'check']"
                    :class="diffLooksSuspicious ? 'text-red-500' : 'text-green-600'"
                    style="font-size: 11px; margin-top: 2px;"
                  />
                  <div class="flex-1">
                    <p class="text-xs font-semibold" :class="diffLooksSuspicious ? 'text-red-800' : 'text-green-800'">
                      {{ diffSummary }}
                    </p>
                    <p v-if="diffLooksSuspicious" class="text-[11px] text-red-700 mt-0.5">
                      That looks like more than you asked for. Review carefully, or hit Revert and rephrase.
                    </p>

                    <ul class="mt-1.5 space-y-0.5">
                      <li v-for="f in aiDiff.added" :key="`a-${f.id}`" class="text-[11px] text-green-800">
                        <span class="font-mono font-semibold">+ {{ f.id }}</span>
                        <span class="text-gray-500"> ({{ f.type }})</span>
                        <span v-if="f.question" class="text-gray-600"> — {{ f.question }}</span>
                      </li>
                      <li v-for="f in aiDiff.removed" :key="`r-${f.id}`" class="text-[11px] text-red-800">
                        <span class="font-mono font-semibold">− {{ f.id }}</span>
                        <span class="text-gray-500"> ({{ f.type }})</span>
                        <span class="text-red-600 font-medium"> — removed</span>
                      </li>
                      <li v-for="f in aiDiff.changed" :key="`c-${f.id}`" class="text-[11px] text-amber-800">
                        <span class="font-mono font-semibold">~ {{ f.id }}</span>
                        <span class="text-gray-500"> — changed {{ f.keys.join(', ') }}</span>
                      </li>
                    </ul>

                    <p v-if="aiDiff.unchanged" class="text-[11px] text-gray-500 mt-1">
                      {{ aiDiff.unchanged }} existing field{{ aiDiff.unchanged === 1 ? '' : 's' }} left untouched.
                    </p>
                  </div>
                  <button @click="revertToBaseline" class="px-2 py-1 text-[11px] text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 whitespace-nowrap">
                    Revert
                  </button>
                </div>
              </div>

              <!-- Feedback -->
              <div v-if="showFeedbackInput" class="bg-yellow-50 border border-yellow-200 rounded p-3">
                <textarea v-model="aiFeedback" rows="3" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" :placeholder="aiEditingId ? 'e.g. Add a &quot;Description&quot; free text field' : 'Describe what needs improvement…'"></textarea>
                <div class="flex justify-end gap-2 mt-2">
                  <button @click="showFeedbackInput = false; aiFeedback = ''" class="px-3 py-1 text-xs text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                  <button @click="regenerateWithFeedback" :disabled="!aiFeedback || aiGenerating" class="px-3 py-1 text-xs text-white bg-yellow-600 rounded hover:bg-yellow-700 disabled:opacity-50">{{ aiGenerating ? 'Working…' : (aiEditingId ? 'Apply change' : 'Regenerate') }}</button>
                </div>
              </div>

              <!-- Config Preview -->
              <div>
                <div class="flex items-center gap-2 mb-1">
                  <span style="font-size: 11px;" class="font-medium text-gray-500 uppercase tracking-wide">Configuration</span>
                  <span class="flex-1" />
                  <button @click="editGeneratedConfig" style="font-size: 11px;" class="text-blue-600 hover:text-blue-700 font-medium">Edit JSON</button>
                </div>
                <div class="bg-gray-900 rounded p-3 max-h-48 overflow-y-auto">
                  <pre class="text-xs text-gray-200 font-mono whitespace-pre-wrap">{{ formatConfig(aiConfiguration, true) }}</pre>
                </div>
              </div>

              <!-- Fields -->
              <div v-if="aiConfiguration?.fields">
                <span style="font-size: 11px;" class="font-medium text-gray-500 uppercase tracking-wide">Fields ({{ aiConfiguration.fields.length }})</span>
                <div class="mt-1 border border-gray-100 rounded">
                  <div v-for="(field, i) in aiConfiguration.fields" :key="field.id" class="flex items-center gap-2 px-3 py-1.5" :class="i < aiConfiguration.fields.length - 1 ? 'border-b border-gray-100' : ''">
                    <span class="flex-1 text-gray-700" style="font-size: 12px;">{{ field.question }}</span>
                    <span class="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600 font-mono" style="font-size: 10px;">{{ field.type }}</span>
                    <span v-if="field.required" class="text-red-500" style="font-size: 10px;">*</span>
                    <font-awesome-icon v-if="field.condition" :icon="['fas', 'code-branch']" class="text-purple-400" style="font-size: 10px;" />
                  </div>
                </div>
              </div>
            </div>

            <div class="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-between">
              <div class="flex gap-2">
                <button @click="showFeedbackInput = !showFeedbackInput" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Improve</button>
                <button v-if="!aiEditingId" @click="regenerateWithAI" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Start Over</button>
              </div>
              <div class="flex gap-2">
                <button @click="closeAIGenerator" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                <button @click="acceptAIGenerated" class="px-3 py-1.5 text-xs text-white bg-green-600 rounded hover:bg-green-700">{{ aiEditingId ? 'Save changes' : 'Accept & Create' }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Work Type Modal -->
    <transition name="modal">
      <div v-if="showWorkTypeModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">{{ editingWorkType ? 'Edit' : 'Create' }} Work Type</h3>
          </div>
          <div class="p-4 space-y-3">
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Name *</label>
              <input v-model="workTypeForm.name" type="text" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500" placeholder="Work type name">
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Display Name</label>
              <input v-model="workTypeForm.display_name" type="text" class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500" placeholder="Optional display name">
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Abbreviation</label>
              <input v-model="workTypeForm.abbreviated_name" type="text" maxlength="10" class="w-32 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 font-mono" placeholder="e.g., MAC" @input="workTypeForm.abbreviated_name = workTypeForm.abbreviated_name.toUpperCase()">
            </div>
            <div>
              <div class="flex items-center justify-between mb-1">
                <label class="text-xs font-medium text-gray-700">Configuration</label>
                <button @click="openJsonEditor" style="font-size: 11px;" class="text-blue-600 hover:text-blue-700 font-medium">Edit JSON</button>
              </div>
              <div class="bg-gray-900 rounded p-2 max-h-32 overflow-y-auto">
                <pre class="text-xs text-gray-200 font-mono whitespace-pre-wrap">{{ formatConfig(workTypeForm.configuration) }}</pre>
              </div>
            </div>
          </div>
          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end gap-2">
            <button @click="closeWorkTypeModal" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button @click="saveWorkType" :disabled="!workTypeForm.name" class="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">{{ editingWorkType ? 'Update' : 'Create' }}</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Configuration View Modal -->
    <transition name="modal">
      <div v-if="showConfigModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">{{ viewingWorkType?.name }}</h3>
          </div>
          <div class="p-4">
            <div class="bg-gray-900 rounded p-3 max-h-96 overflow-y-auto">
              <pre class="text-xs text-gray-200 font-mono whitespace-pre-wrap">{{ formatConfig(viewingWorkType?.configuration, true) }}</pre>
            </div>
          </div>
          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end">
            <button @click="closeConfigModal" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Close</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- JSON Editor -->
    <!-- JSON Editor Modal -->
    <transition name="modal">
      <div v-if="showJsonEditor" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-lg">
          <div class="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
            <h3 class="text-sm font-semibold text-gray-900">Edit Configuration</h3>
            <button @click="showJsonEditor = false" class="text-gray-400 hover:text-gray-600">&times;</button>
          </div>
          <div class="p-4">
            <textarea
              v-model="jsonEditorText"
              rows="20"
              class="w-full px-3 py-2 text-xs font-mono border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              :class="jsonEditorError ? 'border-red-400' : ''"
            ></textarea>
            <p v-if="jsonEditorError" class="text-[10px] text-red-500 mt-1">{{ jsonEditorError }}</p>
          </div>
          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end gap-2">
            <button @click="showJsonEditor = false" class="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button @click="saveJsonEditor" :disabled="!!jsonEditorError" class="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">Save</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import tooltip from '@/directives/tooltip';

const SORTABLE_COLUMNS = [
  { key: 'display', label: 'Display Name', value: (wt) => wt.display_name || wt.name },
  { key: 'name', label: 'Name', value: (wt) => wt.name },
  { key: 'abbreviation', label: 'Abbrev.', value: (wt) => wt.abbreviated_name },
];

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

const SYSTEM_TOOLTIP = 'This work type is managed by Kairon. If you need help modifying it, reach out to support@kaironhealth.com.';

export default {
  components: {

    FontAwesomeIcon,
  },
  directives: { tooltip },
  data() {
    return {
      workTypes: [],
      loading: true,
      searchQuery: '',

      showWorkTypeModal: false,
      editingWorkType: null,
      workTypeForm: { name: '', display_name: '', abbreviated_name: '', configuration: {} },

      showConfigModal: false,
      viewingWorkType: null,

      showJsonEditor: false,
      jsonEditorText: '{}',
      jsonEditorSource: null,

      showAIGeneratorModal: false,
      aiEditingId: null,
      aiDescription: '',
      aiWorkTypeName: '',
      aiAbbreviation: '',
      aiGenerating: false,
      aiGenerated: false,
      aiConfiguration: null,
      aiValidation: {},
      aiError: null,
      showFeedbackInput: false,
      aiFeedback: '',
      originalAIDescription: '',
      // Field-level diff from the last AI pass + the instruction that produced
      // it, so the review step can state what actually changed.
      aiDiff: null,
      lastEditInstruction: '',
      // Config as it was when the modal opened — the baseline for "Revert".
      aiBaselineConfiguration: null,
      systemTooltip: SYSTEM_TOOLTIP,
      sortableColumns: SORTABLE_COLUMNS,
      sortKey: 'display',
      sortDirection: 'asc',
    };
  },
  computed: {
    jsonEditorError() {
      if (!this.jsonEditorText) return null;
      try { JSON.parse(this.jsonEditorText); return null; } catch (e) { return e.message; }
    },
    diffSummary() {
      const d = this.aiDiff;
      if (!d) return '';
      const parts = [];
      if (d.added.length) parts.push(`${d.added.length} field${d.added.length === 1 ? '' : 's'} added`);
      if (d.removed.length) parts.push(`${d.removed.length} removed`);
      if (d.changed.length) parts.push(`${d.changed.length} changed`);
      return parts.length ? parts.join(', ') : 'No changes to any field';
    },

    // Heuristic for "the model did more than I asked". A single-field request is
    // the overwhelmingly common case, so >3 additions, or ANY removal of an
    // existing field, is worth a loud warning rather than silent acceptance.
    diffLooksSuspicious() {
      const d = this.aiDiff;
      if (!d) return false;
      return d.added.length > 3 || d.removed.length > 0;
    },

    sortedWorkTypes() {
      const { value } = SORTABLE_COLUMNS.find((column) => column.key === this.sortKey);
      const direction = this.sortDirection === 'asc' ? 1 : -1;
      return [...this.filteredWorkTypes].sort((a, b) => {
        const left = value(a);
        const right = value(b);
        if (!left || !right) return !left - !right;
        return direction * collator.compare(left, right) || collator.compare(a.name, b.name);
      });
    },

    filteredWorkTypes() {
      if (!this.searchQuery) return this.workTypes;
      const q = this.searchQuery.toLowerCase();
      return this.workTypes.filter(wt =>
        wt.name.toLowerCase().includes(q) ||
        (wt.display_name && wt.display_name.toLowerCase().includes(q)) ||
        (wt.abbreviated_name && wt.abbreviated_name.toLowerCase().includes(q))
      );
    },
  },
  methods: {
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDirection = 'asc';
      }
    },
    sortAria(key) {
      if (this.sortKey !== key) return 'none';
      return this.sortDirection === 'asc' ? 'ascending' : 'descending';
    },
    sortIcon(key) {
      if (this.sortKey !== key) return faSort;
      return this.sortDirection === 'asc' ? faSortUp : faSortDown;
    },
    async fetchWorkTypes() {
      try {
        const { data } = await axios.get('/api/worktypes/');
        this.workTypes = data;
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    },

    // AI Generation
    // generate-config runs as a django-tasks background job (2026-07-01) — the
    // LLM call + retries can exceed the ~30s ALB/nginx timeout, which
    // previously surfaced as a 504 "Generation failed" even on requests that
    // would have succeeded given more time. We now enqueue and poll.
    async generateWithAI() {
      if (!this.aiDescription || !this.aiWorkTypeName) { this.aiError = 'Provide both a description and a name'; return; }
      this.originalAIDescription = this.aiDescription;
      this.aiGenerating = true;
      this.aiError = null;
      try {
        const { data } = await axios.post('/api/worktypes/generate-config/', {
          mode: 'generate',
          description: this.aiDescription,
          context: { work_type_name: this.aiWorkTypeName },
        });
        const result = await this._pollJob(data.job_id);
        this.aiConfiguration = result.configuration;
        this.aiValidation = result.validation || {};
        // Fresh generation: the generated config IS the baseline, so the diff
        // panel stays quiet until the user actually edits it.
        this.aiBaselineConfiguration = JSON.parse(JSON.stringify(result.configuration || {}));
        this.aiDiff = null;
        this.lastEditInstruction = '';
        this.aiGenerated = true;
      } catch (e) { this.aiError = e.response?.data?.error || e.message || 'Generation failed'; }
      finally { this.aiGenerating = false; }
    },

    // Applies a targeted change to the config currently on screen.
    //
    // mode:'edit' is essential. It routes the backend to
    // SurveyGenerator.edit_survey, which treats the existing configuration as
    // the ANCHOR and the instruction as the only licence to change anything.
    // Previously this posted to the GENERATE path with the old config as a mere
    // hint, so "add one field 'Description'" against the 0-field Attachments
    // work type came back as a 14-field invented practice-intake form. We also
    // no longer send a fabricated description — on an edit the config IS the
    // requirement, and passing a one-word title ("Attachments") is exactly what
    // the model mistook for a spec.
    async regenerateWithFeedback() {
      if (!this.aiFeedback) return;
      this.aiGenerating = true;
      this.aiError = null;
      try {
        const { data } = await axios.post('/api/worktypes/generate-config/', {
          mode: 'edit',
          feedback: this.aiFeedback,
          previous_configuration: this.aiConfiguration,
          context: { work_type_name: this.aiWorkTypeName },
        });
        const result = await this._pollJob(data.job_id);
        this.aiConfiguration = result.configuration;
        this.aiValidation = result.validation || {};
        this.aiDiff = result.diff || null;
        this.lastEditInstruction = this.aiFeedback;
        this.showFeedbackInput = false;
        this.aiFeedback = '';
      } catch (e) { this.aiError = e.response?.data?.error || e.message || 'Edit failed'; }
      finally { this.aiGenerating = false; }
    },

    // Polls a django-tasks Job until completed/failed. Mirrors the pattern in
    // EngagementSummary.vue / PHM_AssessmentReport.vue.
    //
    // IMPORTANT: a failed status-check GET (network blip, transient 5xx) is
    // NOT the same thing as job.status === 'failed'. The enqueue already
    // succeeded by the time we're polling — the background job is very likely
    // still running fine. Throwing on the first flaky poll wrongly reported
    // "Generation failed" for jobs that were succeeding, because a single bad
    // GET over a up-to-2-minute polling window is far more likely than the
    // generation itself failing. Only an explicit failed job status (or too
    // many CONSECUTIVE poll errors in a row) ends the poll as a failure.
    async _pollJob(jobId) {
      const maxAttempts = 60; // 2s interval -> up to 2 minutes
      const maxConsecutiveErrors = 5; // ~10s of pure network trouble before giving up
      let consecutiveErrors = 0;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 2000));
        let data;
        try {
          ({ data } = await axios.get(`/api/jobs/${jobId}/status/`));
        } catch (e) {
          // 404 means the job genuinely doesn't exist — fail fast, retrying
          // won't help. Anything else (network error, 5xx, ALB hiccup) is
          // transient — keep polling.
          if (e.response?.status === 404) {
            throw new Error('Job not found');
          }
          consecutiveErrors += 1;
          if (consecutiveErrors >= maxConsecutiveErrors) {
            throw new Error('Lost connection while checking generation status. The job may still complete — reopen this dialog in a moment to check.');
          }
          continue;
        }
        consecutiveErrors = 0;
        if (data.status === 'completed') return data.result_data;
        if (data.status === 'failed') {
          const details = data.result_data?.details;
          throw new Error(data.error_message || (details ? JSON.stringify(details) : 'Generation failed'));
        }
      }
      throw new Error('Generation is taking longer than expected. The job may still complete — reopen this dialog in a moment to check.');
    },

    regenerateWithAI() {
      this.aiGenerated = false; this.aiConfiguration = null; this.aiValidation = {};
      this.showFeedbackInput = false; this.aiFeedback = '';
      this.generateWithAI();
    },

    editGeneratedConfig() { this.jsonEditorText = JSON.stringify(this.aiConfiguration || {}, null, 2); this.jsonEditorSource = 'ai'; this.showJsonEditor = true; },

    // AI Edit: open the same AI modal seeded with an existing work type's
    // config, straight into the review step. The "Improve" (feedback) loop
    // regenerates against previous_configuration exactly as in create mode;
    // the terminal action PATCHes the existing row instead of creating one.
    aiEditWorkType(wt) {
      if (wt.is_system) return;
      this.aiEditingId = wt.id;
      this.aiWorkTypeName = wt.name;
      this.aiAbbreviation = wt.abbreviated_name || '';
      this.aiConfiguration = wt.configuration || {};
      // Baseline for the diff + Revert. Deep-cloned so later edits to
      // aiConfiguration can't mutate it.
      this.aiBaselineConfiguration = JSON.parse(JSON.stringify(wt.configuration || {}));
      this.aiValidation = {};
      this.aiDiff = null;
      this.lastEditInstruction = '';
      // NOTE: deliberately NOT seeding a description from the title. Edits post
      // mode:'edit' with the config as the anchor; a one-word pseudo-description
      // ("Attachments") is what the model previously mistook for a spec and
      // answered with 14 invented fields.
      this.originalAIDescription = '';
      this.aiGenerated = true;      // jump past the description-first step
      this.showFeedbackInput = true; // land in the Improve box, ready to type
      this.aiError = null;
      this.showAIGeneratorModal = true;
    },

    async acceptAIGenerated() {
      if (!this.aiConfiguration) return;
      try {
        if (this.aiEditingId) {
          await axios.patch(`/api/worktypes/${this.aiEditingId}/`, {
            configuration: this.aiConfiguration,
          });
        } else {
          await axios.post('/api/worktypes/', {
            name: this.aiWorkTypeName,
            display_name: this.aiConfiguration.title || this.aiWorkTypeName,
            abbreviated_name: this.aiAbbreviation || null,
            configuration: this.aiConfiguration,
          });
        }
        this.closeAIGenerator();
        this.fetchWorkTypes();
      } catch (e) { this.aiError = 'Failed to save'; }
    },

    closeAIGenerator() {
      this.showAIGeneratorModal = false; this.aiEditingId = null;
      this.aiDescription = ''; this.originalAIDescription = '';
      this.aiWorkTypeName = ''; this.aiAbbreviation = ''; this.aiGenerated = false;
      this.aiConfiguration = null; this.aiValidation = {}; this.aiError = null;
      this.showFeedbackInput = false; this.aiFeedback = '';
      this.aiDiff = null; this.lastEditInstruction = '';
      this.aiBaselineConfiguration = null;
    },

    // Throw away the AI's last result and go back to the config as it was when
    // the modal opened. The escape hatch for "that's not what I asked for" —
    // previously the only way out was Cancel and start over.
    revertToBaseline() {
      if (!this.aiBaselineConfiguration) return;
      this.aiConfiguration = JSON.parse(JSON.stringify(this.aiBaselineConfiguration));
      this.aiDiff = null;
      this.lastEditInstruction = '';
      this.aiError = null;
    },

    // CRUD
    editWorkType(wt) {
      if (wt.is_system) return;
      this.editingWorkType = wt;
      this.workTypeForm = { name: wt.name, display_name: wt.display_name || '', abbreviated_name: wt.abbreviated_name || '', configuration: wt.configuration || {} };
      this.showWorkTypeModal = true;
    },
    async saveWorkType() {
      try {
        const d = { name: this.workTypeForm.name, display_name: this.workTypeForm.display_name || null, abbreviated_name: this.workTypeForm.abbreviated_name || null, configuration: this.workTypeForm.configuration };
        if (this.editingWorkType) { await axios.patch(`/api/worktypes/${this.editingWorkType.id}/`, d); }
        else { await axios.post('/api/worktypes/', d); }
        this.closeWorkTypeModal();
        this.fetchWorkTypes();
      } catch (e) { console.error(e); }
    },
    viewConfiguration(wt) { this.viewingWorkType = wt; this.showConfigModal = true; },
    openJsonEditor() {
      const src = this.workTypeForm.configuration || {};
      this.jsonEditorText = JSON.stringify(src, null, 2);
      this.jsonEditorSource = 'worktype';
      this.showJsonEditor = true;
    },
    saveJsonEditor() {
      if (this.jsonEditorError) return;
      const parsed = JSON.parse(this.jsonEditorText);
      if (this.jsonEditorSource === 'worktype') this.workTypeForm.configuration = parsed;
      else if (this.jsonEditorSource === 'ai') this.aiConfiguration = parsed;
      this.showJsonEditor = false;
    },
    closeWorkTypeModal() { this.showWorkTypeModal = false; this.editingWorkType = null; this.workTypeForm = { name: '', display_name: '', abbreviated_name: '', configuration: {} }; },
    closeConfigModal() { this.showConfigModal = false; this.viewingWorkType = null; },
    formatConfig(config, expanded = false) {
      if (!config || Object.keys(config).length === 0) return '{}';
      return JSON.stringify(config, null, expanded ? 2 : 0);
    },
  },
  mounted() { this.fetchWorkTypes(); },
};
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
